import { ReactNode, useContext } from 'react';
import { Close, InsertChart, Refresh, RouteOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

import { TargetNameAndDescription } from './TargetNameAndDescription';
import { useNavigate } from 'react-router';
import { MinimalTarget } from '@/generated/model';
import { ctxUser } from '@/contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { NewFeatureBadge } from '@/shared/ui/new-feature-badge/NewFeatureBadge';
import { TFunction } from 'i18next';

type FastAccessButton = {
  icon: ReactNode;
  title: string;
  path: string;
  releaseDate?: string;
  isVisible?: boolean;
};

function FastAccessButtons({ target, buttons }: { target: MinimalTarget; buttons: FastAccessButton[] }) {
  const navigate = useNavigate();

  return (
    <Stack direction="row" gap={3} sx={{ flexShrink: 0 }}>
      {buttons
        .filter((button) => button.isVisible ?? true)
        .map((button) => (
          <Tooltip key={button.path} title={button.title} placement={isPhoneOrHighScreen() ? 'top' : 'right'}>
            <IconButton
              onClick={() =>
                navigate(button.path, {
                  state: { target },
                })
              }
              size="small"
              color="primary"
              sx={{
                height: 'fit-content',
                background: 'var(--mui-palette-grey-200)',
                boxShadow: '0 0 10px rgb(2 136 209)',
              }}
            >
              {button.releaseDate ? (
                <NewFeatureBadge releaseDate={button.releaseDate}>{button.icon}</NewFeatureBadge>
              ) : (
                button.icon
              )}
            </IconButton>
          </Tooltip>
        ))}
    </Stack>
  );
}

function getFastAccessButtons(
  t: TFunction,
  userHasTacho: boolean | undefined,
  hasTargetMetadataId: boolean | undefined
): FastAccessButton[] {
  const buttons = [
    {
      icon: <RouteOutlined sx={{ transform: 'rotate(90deg)' }} />,
      title: t('tracking.fastAccessTooltip'),
      path: '/routes/select',
      releaseDate: '2026-03-22',
    },
  ] satisfies FastAccessButton[];

  if (hasTargetMetadataId) {
    if (userHasTacho) {
      buttons.push({
        icon: <InsertChart />,
        title: t('tracking.fastAccessTooltipChart'),
        path: '/tachograph/chart',
        releaseDate: '2026-07-22',
      });
    }
  }

  return buttons;
}

export function HeaderNameAndIcons({
  target,
  onReload,
  onClose,
  hasTargetMetadataId,
}: {
  target: MinimalTarget;
  onReload?: () => void;
  onClose: () => void;
  hasTargetMetadataId?: boolean;
}) {
  const userCtx = useContext(ctxUser);
  const isTracking = onReload !== undefined;
  const userHasTacho = userCtx?.user.hasTachograph;

  const { t } = useTranslation();

  const buttons = getFastAccessButtons(t, userHasTacho, hasTargetMetadataId);

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={isPhoneOrHighScreen() ? { width: '100%', pl: 1 } : { width: '100%' }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <TargetNameAndDescription
          targetName={target.name}
          targetDescription={target.description}
          styleClassName={'target-name-and-description-header'}
          truncate
        />
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        {!userCtx?.isToken() && isTracking && <FastAccessButtons target={target} buttons={buttons} />}
        {onReload !== undefined && (
          <IconButton color="primary" onClick={onReload} data-qa-element-id="popup_refresh_button">
            <Refresh />
          </IconButton>
        )}
        <IconButton color="primary" onClick={onClose} data-qa-element-id="popup_close_button">
          <Close />
        </IconButton>
      </Stack>
    </Stack>
  );
}
